import { isNil } from "lodash";
import { useGrowl } from "PFApp/use_growl";
import { Alert } from "PFComponents/alert";
import ConfirmModal from "PFComponents/confirm/confirm_modal";
import { Icon } from "PFComponents/icon";
import AutoSelect from "PFComponents/select/autoselect";
import { Typography } from "PFComponents/typography";
import { useMergeCustomValues } from "PFCore/hooks/queries/custom_fields/admin/custom_values_bulk/use_merge_custom_values";
import { fetchCustomValuesOptions } from "PFCore/services/custom_values";
import { CustomValue, PureCustomValue } from "PFTypes";
import React, { useCallback, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";

import { useBulkResponseHandler } from "../../../table/hooks/use_bulk_response_handler";
import { useSkillManagementModal } from "../../../table/hooks/use_skill_management_modal";
import { useSkillsCustomTypeId } from "../../../utils/use_skills_custom_type";
import css from "../skills_management_modal.module.scss";

const getSkillNames = (skills: CustomValue[]): string => {
  const skillNames = skills
    .map((skill) => {
      const skillName = String(skill.value);
      if (skillName.length < 30) {
        return skillName;
      }
      return `${skillName.slice(0, 30)}...`;
    })
    .join(", ");

  return skillNames;
};

export const MergeSkillModal = () => {
  const { t: defaultT } = useTranslation("translation");
  const { t } = useTranslation("skillsManagement", { keyPrefix: "header.modals" });
  const growl = useGrowl();
  const { modalData, closeModal } = useSkillManagementModal();
  const customTypeId = useSkillsCustomTypeId();
  const { mutateAsync: mergeSkills } = useMergeCustomValues();
  const handleResponse = useBulkResponseHandler();

  const [targetSkill, setTargetSkill] = useState<PureCustomValue | null>(null);
  const [skillAutocompleteError, setSkillAutocompleteError] = useState<string>("");

  const skillIds = useMemo(() => modalData.skills?.map(({ id }) => id) || [], [modalData.skills]);

  const handleOk = useCallback(
    () =>
      new Promise<void>((resolve) => {
        if (isNil(targetSkill)) {
          setSkillAutocompleteError(t("errors.selectASkill"));
        } else {
          mergeSkills(
            { customTypeId, ids: modalData.skills?.map(({ id }) => id) || [], targetSkillId: targetSkill.id },
            {
              onSuccess: (response) => {
                handleResponse(response, { targetSkill });
                resolve();
              },
              onError: () => growl({ kind: "error", message: defaultT("somethingWentWrong") })
            }
          );
        }
      }),
    [customTypeId, mergeSkills, modalData.skills, targetSkill, handleResponse, growl, defaultT, t]
  );

  if (!modalData.skills) {
    return <Redirect to="/admin/skills_management" />;
  }

  return (
    <ConfirmModal handleOK={handleOk} handleClose={closeModal} labelOK={t("merge.action")}>
      <div className={css.mergeModalContent}>
        <Typography variant="h4" className={css.title}>
          {t("merge.title")}
        </Typography>
        <Typography className={css.skillsList} variant="h5">
          {getSkillNames(modalData.skills!)}
        </Typography>
        <div className={css.mainSection}>
          <Icon size="xl" name="merge" transform="rotate(90)" />
          <Typography variant="bodyRegular" className={css.iconLabel}>
            {t("merge.mergeWith")}
          </Typography>
          <AutoSelect
            values={!isNil(targetSkill) ? [targetSkill] : null}
            controlledValue
            closeOnChange
            query={(term: string) => fetchCustomValuesOptions({ term, type: "skills" })}
            parseResponse={(response) => response?.entries || []}
            filterOptions={(entries: PureCustomValue[]) =>
              entries.filter(({ id }) => !skillIds.includes(Number(id)))
            }
            label={t("merge.searchForASkill")}
            handleChange={(values: PureCustomValue[]) => {
              setTargetSkill(values[0]);
              setSkillAutocompleteError("");
            }}
            error={skillAutocompleteError}
            rootClassName={css.skillDropdown}
          />
          <Alert
            hideIcon
            small
            className={css.warningBox}
            title={
              <Trans
                t={t}
                i18nKey="merge.warningBox"
                values={{ skillNames: getSkillNames(modalData.skills!) }}
                components={[<Typography variant="bodyBold" tag="span" key="0" />]}
              />
            }
          />
        </div>
      </div>
    </ConfirmModal>
  );
};
